import { createContext, useContext, useState, useEffect } from "react";
import { useWebSocketActions } from "../context/WebSocketContext";
import api from "../services/api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [access, setAccess] = useState(localStorage.getItem("access"));
  const [refresh, setRefresh] = useState(localStorage.getItem("refresh"));
  const [user, setUser] = useState(null);

  const isAuthenticated = !!access;
  const { startWebSocket, stopWebSocket, sendJsonMessage } = useWebSocketActions();

  const login = (accessToken, refreshToken, userData = null) => {
    localStorage.setItem("access", accessToken);
    localStorage.setItem("refresh", refreshToken);
    setAccess(accessToken);
    setRefresh(refreshToken);

    if (userData) {
      setUser(userData);
      startWebSocket(); // ✅ conecta WebSocket tras login
    } else {
      fetchUserProfile(accessToken);
    }
  };

  // HAPPYCAP_RELIABLE_PHOTO_LOGOUT_V1
  const logout = async () => {
    // Registrar primero el cierre de sesión en Django.
    // No depender de que el WebSocket siga conectado.
    await api.delete("/chat/borrar-mensajes/");

    // Limpiar mensajes locales y credenciales solo tras confirmar.
    Object.keys(localStorage)
      .filter((key) => key.startsWith("chat_messages_"))
      .forEach((key) => localStorage.removeItem(key));

    localStorage.removeItem("chat_modal_acknowledged");
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");

    setAccess(null);
    setRefresh(null);
    setUser(null);

    stopWebSocket();
  };

  const fetchUserProfile = async (token = access) => {
    try {
      const res = await fetch("/api/me", {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Error al obtener perfil");
      const data = await res.json();
      setUser(data);
      startWebSocket(); // ✅ conecta WebSocket si perfil exitoso
    } catch (err) {
      console.error("❌ Error obteniendo el perfil:", err);
      logout(); // opción segura si el perfil no se puede cargar
    }
  };

  useEffect(() => {
    if (access && !user) {
      fetchUserProfile();
    }

    const sync = () => {
      setAccess(localStorage.getItem("access"));
      setRefresh(localStorage.getItem("refresh"));
    };

    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [access, user]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
